import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox'
import React from 'react'
import styled from 'styled-components'
import { Check, Minus } from 'sutra/components/Icons'
import { accents, brand, semantic } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { typography } from 'sutra/tokens/typography'

export interface CheckboxProps {
  // Controlled when set; omit it (and use `defaultChecked`) for uncontrolled.
  checked?: boolean
  defaultChecked?: boolean
  // Receives the next value, not an event (antd's `e.target.checked`).
  onChange?: (checked: boolean) => void
  // A mixed state; Base UI exposes it as `aria-checked="mixed"`.
  indeterminate?: boolean
  disabled?: boolean
  // Identifies the item inside a `CheckboxGroup`; ignored outside one.
  value?: string
  id?: string
  name?: string
  'aria-label'?: string
  // Lands on the `role="checkbox"` control, even when `children` are given.
  'data-testid'?: string
  // Land on the outermost element: the label when `children` are given,
  // otherwise the control itself.
  className?: string
  style?: React.CSSProperties
  // Lands on the control, so `e.stopPropagation()` keeps a click off an
  // ancestor row.
  onClick?: React.MouseEventHandler<HTMLElement>
  // Label content; clicking it toggles the checkbox.
  children?: React.ReactNode
}

const Label = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.text.sm.fontSize}px;
  line-height: ${typography.text.sm.lineHeight}px;
  color: ${semantic.fg};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`

// `flex: 1; min-width: 0` lets a `styled(Checkbox)` that makes the label
// `display: flex; width: 100%` use the whole row as the click target.
const LabelText = styled.span`
  flex: 1;
  min-width: 0;
`

const Box = styled(BaseCheckbox.Root)`
  box-sizing: border-box;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid #d5d7da;
  background: ${brand.white};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition:
    background ${motion.duration.fast}ms ${motion.ease},
    border-color ${motion.duration.fast}ms ${motion.ease};

  &[data-checked],
  &[data-indeterminate] {
    border-color: ${accents.reflectionBlue[500]};
    background: ${accents.reflectionBlue[500]};
  }

  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }
`

// Sized from the box, so a `styled(Checkbox)` that resizes the box resizes
// the mark with it.
const Mark = styled(BaseCheckbox.Indicator)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  svg {
    width: 78%;
    height: 78%;
  }
`

export const Checkbox = ({
  checked,
  defaultChecked,
  onChange,
  indeterminate,
  disabled,
  value,
  id,
  name,
  'aria-label': ariaLabel,
  'data-testid': testId,
  className,
  style,
  onClick,
  children,
}: CheckboxProps): React.ReactElement => {
  const hasLabel = children !== undefined && children !== null
  const control = (
    <Box
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={(next) => onChange?.(next)}
      indeterminate={indeterminate}
      disabled={disabled}
      value={value}
      id={id}
      name={name}
      aria-label={ariaLabel}
      data-testid={testId}
      className={hasLabel ? undefined : className}
      style={hasLabel ? undefined : style}
      onClick={onClick}
    >
      <Mark>
        {indeterminate ? (
          <Minus color={brand.white} />
        ) : (
          <Check color={brand.white} />
        )}
      </Mark>
    </Box>
  )

  if (!hasLabel) return control

  return (
    <Label $disabled={disabled === true} className={className} style={style}>
      {control}
      <LabelText>{children}</LabelText>
    </Label>
  )
}

Checkbox.displayName = 'Checkbox'
